<template>
  <StandardButton
    v-bind="buttonProps"
    @click="toggleIsMenuShown"
  >
    <slot name="button-content">
      {{ buttonProps.label }}
    </slot>
  </StandardButton>
  <!-- Cannot use StandardMenu here because we need to fully control the container and its styling.
       Cannot use StandardPopover here because we don't want to have an arrow, etc -->
  <PVPopover
    ref="popoverRef"
    :pt="ptPopover"
    :base-z-index="dropdownProps.baseZIndex"
    @show="onPopoverShow"
    @hide="$emit('hide')"
  >
    <slot name="dropdown-content" />
  </PVPopover>
</template>
<script lang="ts">
export default {
  name: 'StandardButtonWithFreeformDropdown',
  compatConfig: { MODE: 3 },
};
</script>
<script setup lang="ts">
import type { StandardButtonProps } from '@/types/StandardButtonTypes';
import type { StandardDropdownProps } from '@/types/StandardDropdownProps';
import { getAlignDropdownRightUtils, DropdownInstance } from '@/utils/getAlignDropdownRightUtils';
import type { PassThrough } from '@primevue/core';
import type { PopoverPassThroughOptions } from 'primevue/popover';
import { ref, computed } from 'vue';

type FreeformPopoverInstance = {
  toggle: (event: Event, target?: HTMLElement) => void;
  hide: () => void;
} & DropdownInstance;

type FreeformDropdownProps = StandardDropdownProps & {
  onBeforeShow?: () => Promise<void> | void;
};

const props = withDefaults(
  defineProps<{
    buttonProps: StandardButtonProps;
    dropdownProps?: FreeformDropdownProps;
  }>(),
  {
    dropdownProps: () => ({
      dropdownAlign: 'start',
    }),
  },
);

const popoverRef = ref<FreeformPopoverInstance | null>(null);

const { alignMenuWithEnd, ptOverride: ptPopoverOverride } = getAlignDropdownRightUtils({ dropdownRef: popoverRef });

const ptPopover = computed(
  (): PassThrough<PopoverPassThroughOptions> => ({
    root: {
      class: ['hide-arrow', 'shadow']
        .concat(props.dropdownProps.rootClass ?? [])
        .filter(Boolean)
        .join(' '),
      style: {
        // match the margin-top of the dropdown-menu in BootstrapVue
        // this is the distance between the dropdown trigger and the dropdown menu
        marginTop: '0.125rem',
      },
    },
    content: {
      class: props.dropdownProps.contentClass ?? [],
    },
    ...ptPopoverOverride,
  }),
);

const emit = defineEmits<{
  (e: 'show'): void;
  (e: 'hide'): void;
}>();

const toggleIsMenuShown = async (e: MouseEvent) => {
  await props.dropdownProps.onBeforeShow?.();

  popoverRef.value?.toggle(e);
};

const hide = () => {
  popoverRef.value?.hide();
};

/**
 * on show, PVPopovers call their `alignOverlay` function, meaning that in order to get the menu to align correctly,
 * we need to call our `alignMenuWithEnd` function *after* the show event.
 */
const onPopoverShow = () => {
  if (props.dropdownProps.dropdownAlign === 'end') {
    alignMenuWithEnd({ shouldDropdownMatchTriggerWidth: props.dropdownProps.shouldMatchTriggerWidth });
  }

  emit('show');
};

defineExpose({
  hide,
});
</script>
<style lang="scss">
// Unscoped on purpose: PVPopover portals to <body>, so the overlay is not under this component's DOM.
.p-popover.hide-arrow:before,
.p-popover.hide-arrow:after {
  display: none !important;
}
</style>
